import type { CSSProperties, ReactNode } from 'react'
import { relativeAge } from '@bloo-team/api-client'
import type { CheckLink } from '../lib/fabric-overview'
import type { Priority, ReadState } from '../lib/plant'

/**
 * Small presentation parts shared by the plant, its detail panels and the
 * Inspect drawer (docs/fabric-overview.md §The plant). Props in, no data.
 *
 * ISA-101 discipline: priority is a SHAPE and a colour, never colour alone —
 * high ◆, medium ▲, info ●. Grey is the normal state; only abnormal rows carry
 * a tone.
 */

export type RenderLink = (link: CheckLink, children: ReactNode, label: string) => ReactNode

export type InspectTarget =
  | 'devices' | 'streams' | 'pending' | 'types' | 'ddm-empty' | 'ddm-missing' | 'disk' | 'alarms'

export const PRIORITY_LABEL: Record<Priority, string> = { high: 'high priority', medium: 'medium priority', info: 'information' }

/** ◆ high · ▲ medium · ● info, drawn so the shape survives without colour. */
export function PriorityGlyph({ priority, label }: { priority: Priority; label?: string }) {
  const title = label ?? PRIORITY_LABEL[priority]
  return (
    <svg className="pl-glyph" data-priority={priority} viewBox="0 0 12 12" role="img" aria-label={title}>
      {priority === 'high' ? <path d="M6 0.5L11.5 6 6 11.5 0.5 6z" /> : priority === 'medium' ? <path d="M6 1L11.5 11H0.5z" /> : <circle cx="6" cy="6" r="4" />}
    </svg>
  )
}

/** A proportion bar; `share` is 0..1. Decorative — the number beside it carries the value. */
export function Share({ share, tone }: { share: number; tone?: 'warn' | 'error' }) {
  const style = { '--pl-share': String(Math.max(0, Math.min(1, share))) } as CSSProperties
  return <span className="pl-share" data-tone={tone} style={style} aria-hidden="true"><span className="pl-share-fill" /></span>
}

/** A level gauge: `value` of `max`, read as "value / max" beside it. */
export function Gauge({ value, max, label }: { value: number; max: number; label: string }) {
  const share = max > 0 ? value / max : 0
  const style = { '--pl-share': String(Math.max(0, Math.min(1, share))) } as CSSProperties
  return (
    <div className="pl-gauge" role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={value} aria-label={label}>
      <span className="pl-gauge-track" style={style}><span className="pl-gauge-fill" /></span>
      <span className="pl-gauge-read"><b>{value}</b> / {max}</span>
    </div>
  )
}

/** Compact age from a timestamp in ms, e.g. "3d"; em dash when unknown. */
export function ageText(ms: number | null, now: number): string {
  return ms === null ? '—' : (relativeAge(ms / 1000, now) ?? '—')
}

/** Compact age from an elapsed duration in ms (an alarm's `ageMs`); em dash when unknown. */
export function elapsedText(ageMs: number | null, now: number): string {
  return ageMs === null ? '—' : ageText(now - ageMs, now)
}

/** "+N more" as a button into the Inspect drawer. Renders nothing when nothing is hidden. */
export function FoldButton({ hidden, noun, onInspect }: { hidden: number; noun: string; onInspect: () => void }) {
  if (hidden <= 0) return null
  return (
    <button type="button" className="pl-fold" onClick={onInspect}>
      +{hidden} more {noun} <span className="pl-fold-hint">inspect</span>
    </button>
  )
}

/** The read behind a unit, as the strip words it: loading… / unavailable. */
export function ReadNote({ state, what }: { state: ReadState; what: string }) {
  if (state === 'ok') return null
  return <p className="hud-unit pl-read" role="status">{state === 'loading' ? 'loading…' : `${what} unavailable`}</p>
}
